import { Alert, Skeleton } from 'antd';
import { useEffect, useRef } from 'react';
import { useCreateThreadMutation, useThreads } from '@entities/chat';
import { ChatPanel } from '@features/test-chat';
import { extractErrorMessage } from '@shared/api';

interface Props {
  assistantId: string;
}

/**
 * Таб «Тест-чат».
 *
 * Стратегия выбора треда:
 * 1) Грузим список тредов ассистента.
 * 2) Если треды есть — открываем самый свежий (бэк сортирует DESC).
 * 3) Если тредов нет — создаём новый тред автоматически при первом
 *    открытии вкладки. Это даёт чистый UX «открыл — пиши», без лишнего
 *    «начать диалог» промежуточного экрана.
 *
 * Полноценный селектор тредов и создание новых отложены — backlog.
 */
export function TestChatTab({ assistantId }: Props) {
  const { data: threads, isLoading: threadsLoading, error: threadsError } =
    useThreads(assistantId);
  const createMutation = useCreateThreadMutation(assistantId);

  // Защита от двойного создания: useEffect срабатывает дважды в React 18
  // strict mode, и без флага мы создаём 2 пустых треда.
  const creationStarted = useRef(false);

  const activeThreadId = threads && threads.length > 0 ? threads[0].id : undefined;

  useEffect(() => {
    if (
      !threadsLoading &&
      !threadsError &&
      threads &&
      threads.length === 0 &&
      !creationStarted.current
    ) {
      creationStarted.current = true;
      createMutation.mutate();
    }
  }, [threads, threadsLoading, threadsError, createMutation]);

  if (threadsLoading) {
    return <Skeleton active paragraph={{ rows: 4 }} />;
  }

  if (threadsError) {
    return (
      <Alert
        type="error"
        showIcon
        message="Не удалось загрузить тред"
        description={extractErrorMessage(threadsError)}
      />
    );
  }

  if (createMutation.error) {
    return (
      <Alert
        type="error"
        showIcon
        message="Не удалось создать тред"
        description={extractErrorMessage(createMutation.error)}
      />
    );
  }

  if (!activeThreadId) {
    // Тредов нет, идёт создание
    return <Skeleton active paragraph={{ rows: 4 }} />;
  }

  return <ChatPanel threadId={activeThreadId} />;
}
